/**
 * seatPeeks.ts — data for the homepage seat "exhibit" (SeatPeek.tsx).
 *
 * K3 design direction (2026-10-05): each peek is a court EXHIBIT, not a fake app
 * window — square, ruled, flat, tabular (Bloomberg terminal / court exhibit).
 * It answers the seat's ONE question (attorney: what to demand; CPA: what to
 * sign; adjuster: what to hold; data: what's left to chase; mediator: the number
 * between). No tabs, no chrome dots, no pills. Exactly THREE orange atoms across
 * the whole seat view: the seat eyebrow, the decisive figure (here), the CTA.
 *
 * EACH EXHIBIT MIRRORS THE SEAT'S REAL CURRENT no-sidebar surface (SSOT:
 * docs/architecture/SEAT_DASHBOARD_MAP.md). Figures are the canonical FICTIONAL
 * demo case (Summit Ridge, d6d88aea), verified against the LIVE backend; cents
 * are standardized ($155,309.37) so the SAME number reads as a through-line as a
 * visitor switches seats. Client has NO exhibit (live magic-link portal only).
 */

import type { SeatId } from '../../config/seats.config';

export type PeekPattern = 'ledger' | 'register' | 'stat';
/** Register verification grammar — emphasis by weight, never hue (K3). */
export type RegisterState = 'approved' | 'review' | 'todo';

export interface LedgerRow {
  label: string;
  value: string;
  /** muted = a supporting/subtracted line (cream-dim), not the headline figure. */
  muted?: boolean;
  /** accent = the one orange decisive figure in the block. */
  accent?: boolean;
}
export interface RegisterRow {
  label: string;
  status: string;
  state: RegisterState;
}
export interface StatCell {
  label: string;
  value: string;
  /** the one orange cell in the strip. */
  accent?: boolean;
}
/** The decisive figure — orange, under an accounting double rule. */
export interface PeekTotal {
  label: string;
  value: string;
}

export interface SeatPeek {
  exhibit: string; // 'A'..'E'
  /** header-right context, e.g. 'CASE D6D88AEA' or 'WORK DESK'. */
  source: string;
  matterTitle: string;
  matterMeta: string;
  /** matter-block right column: a status pair OR a single stage line (not both). */
  status?: { label: string; value: string };
  stage?: string;
  pattern: PeekPattern;
  ledgerRows?: LedgerRow[];
  registerRows?: RegisterRow[];
  statCells?: StatCell[];
  /** register pattern: the attested figures below the engine register (each ✓),
      shown instead of a single total because pillars are attested separately. */
  attestedLines?: LedgerRow[];
  total?: PeekTotal;
  /** provenance footer — the most court-exhibit move available, one line. */
  provenance: string;
  /** court-figure caption rendered under the exhibit (fills the desktop void). */
  figCaption: string;
}

const ATTORNEY_PEEK: SeatPeek = {
  exhibit: 'A',
  source: 'CASE D6D88AEA',
  matterTitle: 'Summit Ridge Restaurant Group LLC',
  matterMeta: 'FIRE · LOSS NOV 14, 2025 · DUAL METHOD',
  status: { label: 'STATUS', value: 'DRAFT — PENDING CPA ATTESTATION' },
  pattern: 'ledger',
  // The attorney DEMANDS both pillars — the demand is the sum. (The sworn/attested
  // core is the BI net; the CPA seat carries that figure. Each pillar is supported
  // on its own ledger, but the plaintiff's planned demand is the dual-pillar total,
  // consistent with the hero's TOTAL DEMAND.)
  ledgerRows: [
    { label: 'Business interruption — net (attested)', value: '$155,309.37' },
    { label: 'First-party property — RCV', value: '$257,000.00' },
  ],
  total: { label: 'PLANNED DEMAND', value: '$412,309.37' },
  provenance: 'DUAL-PILLAR DEMAND · BI NET + FIRST-PARTY PROPERTY · EACH SUPPORTED ON ITS OWN LEDGER',
  figCaption: 'FIG. A — PLANNED DEMAND · SUMMIT RIDGE RESTAURANT GROUP LLC',
};

const ACCOUNTANT_PEEK: SeatPeek = {
  exhibit: 'B',
  source: 'CASE D6D88AEA',
  matterTitle: 'Summit Ridge Restaurant Group LLC',
  matterMeta: 'ENGINE REVIEW · FIRST-PARTY · DUAL METHOD',
  stage: 'STAGE 3 / 8 — ENGINES',
  pattern: 'register',
  registerRows: [
    { label: 'P&L Reconciliation', status: 'APPROVED', state: 'approved' },
    { label: 'Bottom-Up Loss', status: 'APPROVED', state: 'approved' },
    { label: 'Property Valuation (RCV/ACV)', status: 'APPROVED', state: 'approved' },
    { label: 'Dual-Method Reconciler', status: 'IN REVIEW', state: 'review' },
  ],
  // The CPA attests BOTH pillars (property is an attestable engine — attested when
  // the property review is approved). Each is attested SEPARATELY; never blended.
  attestedLines: [
    { label: 'Business interruption — net (sworn)', value: '$155,309.37', accent: true },
    { label: 'First-party property — RCV', value: '$257,000.00' },
  ],
  provenance: 'EACH PILLAR ATTESTED SEPARATELY — NEVER BLENDED INTO ONE SWORN TOTAL',
  figCaption: 'FIG. B — ATTESTATION REGISTER · SUMMIT RIDGE',
};

const ADJUSTER_PEEK: SeatPeek = {
  exhibit: 'C',
  source: 'CASE D6D88AEA',
  matterTitle: 'Summit Ridge Restaurant Group LLC',
  matterMeta: 'CARRIER ANALYSIS · FIRST-PARTY · DUAL METHOD',
  stage: 'STAGE 1 / 3 — ANALYSIS',
  pattern: 'ledger',
  ledgerRows: [
    { label: 'BI net — actual loss sustained (engines)', value: '$155,309.37' },
    { label: 'Documentation adjustment · 5% reserve', value: '− $7,765.47', muted: true },
  ],
  total: { label: 'CARRIER-SUPPORTED POSITION', value: '$147,543.90' },
  provenance: 'VARIANCE −5% VS ATTORNEY DEMAND · SPECULATIVE-GROWTH ARGUMENT (STRONG)',
  figCaption: 'FIG. C — CARRIER RECONCILIATION BRIDGE · SUMMIT RIDGE',
};

const DATA_SPECIALIST_PEEK: SeatPeek = {
  exhibit: 'D',
  source: 'WORK DESK',
  matterTitle: 'Data work desk',
  matterMeta: '3 ACTIVE CASES IN YOUR QUEUE',
  pattern: 'stat',
  statCells: [
    { label: 'TO CLASSIFY', value: '47' },
    { label: 'IN DATA STAGE', value: '3' },
    { label: 'AWAITING OCR', value: '6' },
    { label: 'AUTO-CLASSIFY', value: '91.5%', accent: true },
  ],
  provenance: 'THE PLATFORM AUTO-REQUESTS EXACTLY THE MISSING DOCS — YOU CHASE NOTHING EXTRA',
  figCaption: 'FIG. D — DATA INTAKE QUEUE · ALL ACTIVE CASES',
};

const NEUTRAL_PEEK: SeatPeek = {
  exhibit: 'E',
  source: 'CASE D6D88AEA',
  matterTitle: 'Summit Ridge Restaurant Group LLC',
  matterMeta: 'NEUTRAL BEST ESTIMATE · NOT A PARTY DEMAND',
  status: { label: 'RECORD', value: 'READ-ONLY' },
  pattern: 'ledger',
  // Grounded in the live Fair Settlement engine (POST /api/v1/mediation/calculate):
  // the P50 "fair" point ($154,105.49) sits between the carrier value ($152,503.78)
  // and the attorney/plaintiff top ($155,309.37) — a computed expected value, NOT
  // the plaintiff number restated. Property stays on its own RCV track, never summed.
  ledgerRows: [
    { label: 'Business interruption — P50 fair', value: '$154,105.49' },
    { label: 'First-party property — RCV (separate track)', value: '$257,000.00', muted: true },
  ],
  total: { label: 'NEUTRAL BEST ESTIMATE', value: '$154,105.49' },
  provenance: 'BI P50 BETWEEN CARRIER + PLAINTIFF · PROPERTY ON ITS OWN LEDGER — THE TWO ARE NEVER SUMMED',
  figCaption: 'FIG. E — NEUTRAL BEST ESTIMATE · SUMMIT RIDGE',
};

const PEEKS: Partial<Record<SeatId, SeatPeek>> = {
  attorney: ATTORNEY_PEEK,
  accountant: ACCOUNTANT_PEEK,
  adjuster: ADJUSTER_PEEK,
  data_specialist: DATA_SPECIALIST_PEEK,
  neutral: NEUTRAL_PEEK,
  // client: none — live magic-link portal only; panel stays text-only.
};

export function getSeatPeek(id: SeatId): SeatPeek | undefined {
  return PEEKS[id];
}
